import React, { useEffect, useState } from "react";
import BreadCumb from "../../components/common/Breadcrumb";
import backArrow from "../../assets/task/back_arrow.svg";
import {
    GET_CUSTOMER_EMAILS,
    GET_MEETING_NOTES,
    DEFECTS,
} from "../../api/query";
import { useQuery } from "@apollo/client";
import { format } from "date-fns";
import PaginationNew from "../../components/common/PaginationNew";
import DateSelector from "../../components/common/DateSelector";
import DefectEmailModal from "../defect/DefectEmailModal";
import PaginationNew2 from "../../components/common/PaginationNew2";
import SearchableTable, {
    TableColumn,
} from "../../components/common/SearchableTable";

interface EmailConversationMessage {
    type: string | null;
    sender: string | null;
    body: string | null;
    time: string | null;
    time_ist: string | null;
}

interface CustomerEmail {
    thread_id: string | null;
    agency_name: string | null;
    agency_id: string | null;
    agency_email: string | null;
    employee_id: string | null;
    nature_of_email: string | null;
    system: string | null;
    replied_by: string | null;
    is_replied: boolean;
    conversation: EmailConversationMessage[];
}

interface CustomerEmailsResponse {
    status: boolean;
    message: string;
    status_code: number;
    customer_emails: CustomerEmail[];
    pagination: {
        total_pages: number;
        current_page: number;
        total_items: number;
        has_next: boolean;
        has_previous: boolean;
    };
}

const formatEmailTimestamp = (value: string | null | undefined) => {
    if (!value) return "--";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleString("en-US", {
        timeZone: "Asia/Kolkata",
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    });
};

// Time part (HH:mm:ss) of the last message in an email thread.
const getEmailTime = (email: CustomerEmail): string =>
    email?.conversation?.at(-1)?.time_ist?.split(" ")?.[1] ?? "";

const parseMeetingJson = (value: any): any => {
    if (value === null || value === undefined || value === "") return null;

    if (typeof value !== "string") return value;

    const cleanedValue = value.trim();

    // Prefer the first fenced JSON block because transcript may contain extra
    // duplicated "Action Items" / "Main Outcome" text after the closing fence.
    const fencedJson = cleanedValue.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
    const candidates = [fencedJson?.[1]?.trim(), cleanedValue].filter(
        Boolean,
    ) as string[];

    for (const candidate of candidates) {
        try {
            return JSON.parse(candidate);
        } catch {
            // Try the next candidate.
        }

        // Also support JSON surrounded by non-JSON text.
        const objectStart = candidate.indexOf("{");
        const objectEnd = candidate.lastIndexOf("}");
        if (objectStart !== -1 && objectEnd > objectStart) {
            try {
                return JSON.parse(candidate.slice(objectStart, objectEnd + 1));
            } catch {
                // Try the next candidate.
            }
        }
    }

    return value;
};

const MeetingBulletList = ({ items }: { items: any[] }) => {
    if (!Array.isArray(items) || items.length === 0) {
        return <p className="muted-text">No information available.</p>;
    }

    return (
        <ul style={{ paddingLeft: "20px", margin: "8px 0" }}>
            {items.map((item, index) => (
                <li key={index} style={{ marginBottom: "8px" }}>
                    {typeof item === "string" ? (
                        item
                    ) : (
                        <>
                            {item.topic && <strong>{item.topic}: </strong>}
                            {item.label && <strong>{item.label}: </strong>}
                            {item.detail || item.summary || item.task || ""}
                            {item.assignee_name && (
                                <div>
                                    <strong>Assigned to:</strong> {item.assignee_name}
                                </div>
                            )}
                            {item.due_date_iso && (
                                <div>
                                    <strong>Due date:</strong> {item.due_date_iso}
                                </div>
                            )}
                        </>
                    )}
                </li>
            ))}
        </ul>
    );
};

const MeetingStructuredContent = ({ data }: { data: any }) => {
    const parsed = parseMeetingJson(data);

    if (parsed === null || parsed === undefined || parsed === "") {
        return <p className="muted-text">No information available.</p>;
    }

    if (typeof parsed === "string") {
        return <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>{parsed}</p>;
    }

    // meeting_summary and action_items are JSON arrays in the supplied API response.
    if (Array.isArray(parsed)) {
        return <MeetingBulletList items={parsed} />;
    }

    const sections = [
        { key: "purpose", title: "Purpose" },
        { key: "main_outcome", title: "Main Outcome" },
        { key: "key_discussions", title: "Key Discussions" },
        { key: "decisions_made", title: "Decisions Made" },
        { key: "challenges_identified", title: "Challenges Identified" },
        { key: "action_items", title: "Action Items" },
        { key: "next_steps", title: "Next Steps" },
    ];

    const hasKnownSections = sections.some(
        ({ key }) => parsed[key] !== undefined && parsed[key] !== null,
    );

    if (!hasKnownSections) {
        return (
            <pre
                style={{
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    fontFamily: "inherit",
                }}
            >
                {JSON.stringify(parsed, null, 2)}
            </pre>
        );
    }

    return (
        <div style={{ display: "grid", gap: "16px" }}>
            {sections.map(({ key, title }) => {
                const value = parsed[key];

                if (value === undefined || value === null || value === "") {
                    return null;
                }

                return (
                    <section key={key}>
                        <h6
                            style={{
                                fontSize: "14px",
                                fontWeight: 700,
                                margin: "0 0 6px",
                                color: "var(--ink)",
                            }}
                        >
                            {title}
                        </h6>

                        {Array.isArray(value) ? (
                            <MeetingBulletList items={value} />
                        ) : (
                            <p style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                                {String(value)}
                            </p>
                        )}
                    </section>
                );
            })}
        </div>
    );
};

const MeetingOverviewSections = ({
    transcript,
    meetingSummary,
    actionItems,
}: {
    transcript: any;
    meetingSummary: any;
    actionItems: any;
}) => {
    // The transcript contains the structured meeting notes (sometimes wrapped in
    // Markdown fences and followed by duplicated text). Prefer it for these sections.
    const notes = parseMeetingJson(transcript);
    const structuredNotes =
        notes &&
        typeof notes === "object" &&
        !Array.isArray(notes) &&
        [
            "purpose",
            "main_outcome",
            "key_discussions",
            "decisions_made",
            "challenges_identified",
            "action_items",
            "next_steps",
        ].some((key) => notes[key] !== undefined);

    const summaryFallback = parseMeetingJson(meetingSummary);
    const sections = [
        { key: "key_discussions", title: "Key Discussions" },
        { key: "decisions_made", title: "Decisions Made" },
        { key: "challenges_identified", title: "Challenges Identified" },
        { key: "action_items", title: "Action Items" },
        { key: "next_steps", title: "Next Steps" },
    ];

    const cardStyle = {
        background: "var(--page)",
        border: "1px solid var(--line)",
        borderRadius: "8px",
        padding: "14px",
        fontSize: "13px",
        lineHeight: 1.7,
        overflowWrap: "anywhere" as const,
    };

    return (
        <div style={{ display: "grid", gap: "20px", marginTop: "28px" }}>
            <section>
                <h5 className="relative font-bold text-xl-dv mt-0 mb-[16px] capitalize dark:text-white">
                    Meeting Summary
                </h5>
                <div style={cardStyle}>
                    {structuredNotes ? (
                        <div style={{ display: "grid", gap: "12px" }}>
                            {notes.purpose && (
                                <div>
                                    <strong>Purpose</strong>
                                    <p style={{ margin: "4px 0 0" }}>{notes.purpose}</p>
                                </div>
                            )}
                            {notes.main_outcome && (
                                <div>
                                    <strong>Main Outcome</strong>
                                    <p style={{ margin: "4px 0 0" }}>{notes.main_outcome}</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <MeetingStructuredContent data={summaryFallback} />
                    )}
                </div>
            </section>

            {sections.map(({ key, title }) => {
                const value =
                    key === "action_items"
                        ? structuredNotes && notes.action_items
                            ? notes.action_items
                            : parseMeetingJson(actionItems)
                        : structuredNotes
                            ? notes[key]
                            : null;

                return (
                    <section key={key}>
                        <h5 className="relative font-bold text-xl-dv mt-0 mb-[16px] capitalize dark:text-white">
                            {title}
                        </h5>
                        <div style={cardStyle}>
                            {value !== null && value !== undefined && value !== "" ? (
                                <MeetingStructuredContent data={value} />
                            ) : (
                                <p className="muted-text" style={{ margin: 0 }}>
                                    No information available.
                                </p>
                            )}
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

interface MeetingNote {
    id: string;
    meeting_id: string;
    meeting_date: string;
    meeting_time_ist: string;
    meeting_duration: string;
    participant_name: string;
    transcript: string | null;
    support_agent: string | null;
    meeting_title: string | null;
    meeting_summary: string | null;
    action_items: unknown;
    meeting_type: string | null;
    agency_name: string | null;
    recording_url: string | null;
}

interface MeetingNotesResponse {
    status: boolean;
    message: string;
    status_code: number;
    meeting_notes: MeetingNote[];
    pagination: {
        total_pages: number;
        current_page: number;
        total_items: number;
        has_next: boolean;
        has_previous: boolean;
    };
}

const formatParticipantName = (value: string | null): string => {
    if (!value) return "--";

    try {
        const parsed = JSON.parse(value);

        if (Array.isArray(parsed)) {
            return (
                parsed
                    .filter((name) => typeof name === "string" && name.trim())
                    .join(", ") || "--"
            );
        }

        return typeof parsed === "string" ? parsed : value;
    } catch {
        return value;
    }
};

const ActionsTable: React.FC = () => {
    const [selectedMeeting, setSelectedMeeting] = useState<MeetingNote | null>(
        null,
    );

    const [selectedEmail, setSelectedEmail] = useState<CustomerEmail | null>(
        null,
    );

    const [meetingCurrentPage, setMeetingCurrentPage] = useState(1);
    const [meetingPageSize, setMeetingPageSize] = useState(8);

    const [emailCurrentPage, setEmailCurrentPage] = useState(1);
    const [emailPageSize, setEmailPageSize] = useState(8);
    const [openDefectEmail, setOpenDefectEmail] = useState<string>("");

    const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
    const [defectPage, setDefectPage] = useState(1);
    const [defectPageSize, setDefectPageSize] = useState(50);

    const {
        data: defectsData,
        loading: defectsLoading,
        error: defectsError,
    } = useQuery(DEFECTS, {
        variables: {
            page: defectPage,
            page_size: defectPageSize,
            is_confirmed: true,
            is_issue: true,
            is_resolved: null,
            start_date: startDate || null,
            end_date: startDate || null,
        },
        skip: !startDate,
        fetchPolicy: "network-only",
    });

    const handleDefectPageChange = (page: number) => {
        setDefectPage(page);
    };

    const handleDefectPageSizeChange = (size: number) => {
        setDefectPageSize(size);
        setDefectPage(1);
    };

    const {
        data: emailData,
        loading: emailLoading,
        error: emailError,
    } = useQuery<{ get_customer_emails: CustomerEmailsResponse }>(
        GET_CUSTOMER_EMAILS,
        {
            variables: {
                date: startDate,
                page: emailCurrentPage,
                page_size: emailPageSize,
            },
            fetchPolicy: "network-only",
        },
    );

    const emailResponse = emailData?.get_customer_emails;
    const customerEmails = emailResponse?.customer_emails ?? [];
    const totalEmailItems = emailResponse?.pagination?.total_items ?? 0;

    const { data, loading, error } = useQuery<{
        get_meeting_notes: MeetingNotesResponse;
    }>(GET_MEETING_NOTES, {
        variables: {
            start_date: startDate,
            page: meetingCurrentPage,
            page_size: meetingPageSize,
        },
        fetchPolicy: "network-only",
    });

    const response = data?.get_meeting_notes;
    const meetings = response?.meeting_notes ?? [];
    const totalItems = response?.pagination?.total_items ?? 0;

    useEffect(() => {
        const isPopupOpen = Boolean(selectedMeeting || selectedEmail);

        if (!isPopupOpen) return;

        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = "hidden";
        document.documentElement.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
        };
    }, [selectedMeeting, selectedEmail]);

    const formatMeetingTime = (time: string) => {
        if (!time) return "--";

        const match = time.match(/^(\d{2}):(\d{2})/);

        if (!match) return time;

        const hours = Number(match[1]);
        const minutes = match[2];

        const period = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;

        return `${String(displayHours).padStart(2, "0")}:${minutes} ${period}`;
    };

    const formatMeetingDate = (date: string) => {
        if (!date) return "--";

        const match = date.match(/^(\d{4})-(\d{2})-(\d{2})/);

        if (match) {
            return `${match[3]}-${match[2]}-${match[1]}`;
        }

        return date;
    };

    const handleMeetingPageChange = (page: number) => {
        setMeetingCurrentPage(page);
    };

    const handleMeetingPageSizeChange = (size: number) => {
        setMeetingPageSize(size);
        setMeetingCurrentPage(1);
    };

    const handleEmailPageChange = (page: number) => {
        setEmailCurrentPage(page);
    };

    const handleEmailPageSizeChange = (size: number) => {
        setEmailPageSize(size);
        setEmailCurrentPage(1);
    };

    // ---------- Column definitions for the shared SearchableTable ----------
    const linkButtonStyle: React.CSSProperties = {
        border: "none",
        background: "transparent",
        padding: 0,
        color: "var(--purple-deep)",
        fontWeight: 600,
        cursor: "pointer",
        textAlign: "left",
    };

    const meetingColumns: TableColumn<MeetingNote>[] = [
        {
            key: "time",
            header: "Time",
            searchValue: (m) => formatMeetingTime(m.meeting_time_ist),
            render: (m) => formatMeetingTime(m.meeting_time_ist),
        },
        {
            key: "client",
            header: "Client / Description",
            searchValue: (m) => m.agency_name,
            render: (m) => (
                <button
                    type="button"
                    onClick={() => setSelectedMeeting(m)}
                    style={linkButtonStyle}
                >
                    {m.agency_name || "Unknown Agency"}
                </button>
            ),
        },
    ];

    const emailColumns: TableColumn<CustomerEmail>[] = [
        {
            key: "time",
            header: "Time",
            searchValue: (e) => formatMeetingTime(getEmailTime(e)),
            render: (e) => (
                <div className="text-xs text-gray-500 flex gap-2 items-center">
                    <span>{formatMeetingTime(getEmailTime(e))}</span>
                </div>
            ),
        },
        {
            key: "client",
            header: "Client / Description",
            searchValue: (e) => e.agency_name,
            render: (e) => (
                <button
                    type="button"
                    onClick={() => setSelectedEmail(e)}
                    style={linkButtonStyle}
                >
                    {e.agency_name || "Unknown Agency"}
                </button>
            ),
        },
    ];

    return (
        <>
            <div className="table-container-wrap">
                <style>{`
        :root {
          --purple: #6B46C1;
          --purple-deep: #553C9A;
          --purple-tint: #F3EFFB;
          --purple-line: #DDD3F3;
          --ink: #1A1D26;
          --ink-2: #5B6270;
          --ink-3: #8C93A3;
          --line: #E6E8EE;
          --line-2: #D3D7E0;
          --page: #F5F6FA;
          --card: #FFFFFF;
          --green: #1A9A5C;
          --green-tint: #E8F7EF;
          --amber: #B7791F;
          --amber-tint: #FFF6E5;
          --red: #C53030;
          --red-tint: #FDECEC;
          --r: 10px;
          --shadow: 0 1px 2px rgba(20, 24, 40, .05), 0 4px 14px rgba(20, 24, 40, .04);
        }

        .table-card {
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: var(--r);
          box-shadow: var(--shadow);
          overflow: hidden;
          height: 420px;
        }
          .table-card .responsive-table-wrapper{
          max-height: 300px;
          }
           .table-card .responsive-table-wrapper-dv{
            height: 750px;
            overflow:auto;
          }

        .table-header {
          padding: 16px 20px;
          border-bottom: 1px solid var(--line);
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #FAFAFD;
        }

        .table-header h2 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--ink);
          display: flex;
          align-items: center;
          gap: 8px;
           font-size: 14px!important;
        }

        .badge-count {
          font-size: 12px;
          background: var(--purple-tint);
          color: var(--purple-deep);
          padding: 2px 8px;
          border-radius: 12px;
          font-weight: 600;
        }

        /* Responsive Table Wrapper */
        .responsive-table-wrapper {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .custom-table {
          width: 100%;
          border-collapse: collapse;
          text-align: left;
          font-size: 13.5px;
       
        }

        .custom-table th {
          background: var(--page);
          color: var(--ink-3);
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: .06em;
          font-weight: 600;
          padding: 12px 16px;
          border-bottom: 1px solid var(--line);
        }

        .custom-table td {
          padding: 12px 16px;
          border-bottom: 1px solid var(--line);
          color: var(--ink);
          vertical-align: middle;
        }

        .custom-table tbody tr:last-child td {
          border-bottom: none;
        }

        .custom-table tbody tr:hover {
          background: #FAFAFD;
        }

        /* Helpers & Badges */
        .kind-badge {
          display: inline-block;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: .06em;
          text-transform: uppercase;
          padding: 2px 6px;
          border-radius: 4px;
        }

        .k-h { background: var(--amber-tint); color: var(--amber); }
        .k-l { background: var(--green-tint); color: var(--green); }

        .btn-sm {
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 600;
          border: 1px solid var(--line-2);
          background: #fff;
          cursor: pointer;
          white-space: nowrap;
        }

        .btn-sm.primary {
          background: var(--purple);
          border-color: var(--purple);
          color: #fff;
        }

        .btn-sm.primary:hover {
          background: var(--purple-deep);
        }

        .btn-sm.ghost {
          border-color: transparent;
          color: var(--ink-2);
        }

        .muted-text {
          color: var(--ink-2);
          font-size: 12.5px;
        }

        .meta-tag {
          font-size: 11.5px;
          color: var(--ink-3);
        }

        .pending-text {
          color: var(--purple-deep);
          font-weight: 600;
        }

        .actions-group {
          display: flex;
          gap: 8px;
        }
          .autobadge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: var(--purple-tint);
            border: 1px solid var(--purple-line);
            color: var(--purple-deep);
            font-size: 11.5px;
            font-weight: 500;
            padding: 3px 10px;
            border-radius: 100px;
            white-space: nowrap;
        }
            .metric {
                display: flex;
                flex-direction: column;
                gap: 2px;
            }
                .metric .v {
                font-size: 24px!important;
                font-weight: 700;
                letter-spacing: -.02em;
                line-height: 1.1;
            }
                .table-card-left-item{
                height:870px!important;
                
                }
               .agency-li-items {
                    background: none!important;
                    padding: 0px!important;
                    border: none!important;
                    text-decoration: none;
                }
                .table-card-left-item .sededulardashborad-alert_pagination .action-page-leftcard{
                    padding-left:16px;
                        position: relative;
                        top: 10px;
                }

                .table-card-left-item  .pagination-blg-dv {
                    position: relative;
                    top: 10px;
                }
                    .actioncard-right-item .action-page-leftcard{
                    padding-left:32px;
                }
                    .table-card-left-item h2{
                    font-size: 14px!important;
                    }
      `}</style>

                <div
                    id="cts-respon-p-dv"
                    className="w-full relative min-h-screen overflow-hidden block flex-row items-start justify-center flex-wrap content-start py-20 px-20 mq991:p-8 mq575:p-4 box-border text-left text-base font-manrope"
                >
                    <BreadCumb />
                    <div className="self-stretch flex flex-row items-center justify-start gap-[24px] text-2xl mb-6">
                        {/* <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" alt="" src={backArrow} /> */}
                        <h3 className="m-0  relative text-inherit font-bold font-inherit inline-block  mq450:text-base dark:text-white">
                            Actions
                        </h3>
                    </div>

                    <div className="flex-1 flex flex-col items-start justify-start gap-6 rounded-lg bg-surface-bg-primary dark:bg-slate-800   p-8 mq575:p-3">
                        <div className="grid grid-cols-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 gap-[24px] self-stretch rounded-lg mb-4 items-end">
                            <DateSelector
                                label="Start Date"
                                selectedDate={startDate}
                                onChange={(date) => {
                                    if (date) {
                                        setStartDate(date);
                                        setMeetingCurrentPage(1);
                                        setEmailCurrentPage(1);
                                        setDefectPage(1);
                                    }
                                }}
                                nothideParsedValue={true}
                                blueIcon={true}
                                maxDate={new Date()}
                            />
                            <div
                                className="col-span-3"
                                style={{ marginLeft: "auto", display: "flex", gap: "22px" }}
                            >
                                <div className="metric">
                                    <span className="v">
                                        {defectsData?.defects?.pagination?.total_items || 0}
                                    </span>
                                    <span className="f">arrived</span>
                                </div>
                                <div className="metric">
                                    <span className="v">{totalItems || 0}</span>
                                    <span className="f">confirmed</span>
                                </div>
                                <div className="metric">
                                    <span className="v">{totalEmailItems || 0}</span>
                                    <span className="f">answered</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-6 w-full">
                            {/* LEFT: Agency / Description */}
                            <div className="flex flex-1">
                                <SearchableTable
                                    title="Agency / Description"
                                    count={defectsData?.defects?.pagination?.total_items || 0}
                                    variant="list"
                                    searchValue={(d: any) =>
                                        `${d.agency_name ?? ""} ${d.severity ?? ""} ${d.defect_summary ?? ""}`
                                    }
                                    renderItem={(defect: any) => (
                                        <>
                                            <div className="flex gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setOpenDefectEmail(String(defect.id))}
                                                    className="relative text-sm font-bold agency-li-items capitalize dark:text-white my-0 text-left hover:underline cursor-pointer"
                                                >
                                                    {defect.agency_name || "-"}
                                                </button>
                                                <span className="meta-tag">
                                                    · {defect.severity || "-"}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="my-0 dark:text-white text-sm">
                                                    {defect?.defect_summary}
                                                </p>
                                            </div>
                                        </>
                                    )}
                                    rows={defectsData?.defects?.defects ?? []}
                                    getRowKey={(d) => d.id}
                                    loading={defectsLoading}
                                    error={defectsError}
                                    loadingMessage="Loading defects..."
                                    errorMessage="Failed to load defects."
                                    emptyMessage="No defects found for the selected date."
                                    cardClassName="table-card-left-item w-full"
                                    wrapperClassName="responsive-table-wrapper-dv"
                                    footer={
                                        <PaginationNew2
                                            currentPage={defectsData?.defects?.pagination?.current_page}
                                            handlePageChange={handleDefectPageChange}
                                            totalPages={defectsData?.defects?.pagination?.total_pages}
                                            paginationLength={defectsData?.defects?.pagination?.total_items}
                                            pageSize={defectPageSize}
                                            onPageSizeChange={handleDefectPageSizeChange}
                                        />
                                    }
                                />
                            </div>

                            {/* RIGHT: Confirmed tonight + Answered in session */}
                            <div className="flex flex-1">
                                <div className="grid gap-6 w-full actioncard-right-item">
                                    <SearchableTable
                                        title="Confirmed tonight"
                                        count={totalItems || 0}
                                        columns={meetingColumns}
                                        rows={meetings}
                                        getRowKey={(m) => m.id}
                                        loading={loading}
                                        error={error}
                                        loadingMessage="Loading meeting notes..."
                                        errorMessage="Failed to load meeting notes. Please try again."
                                        emptyMessage="No confirmed meetings found."
                                        cardClassName="w-full"
                                        footerClassName="absolute bottom-[-10px] right-0 w-full"
                                        footerStyle={{
                                            padding: "12px 16px",
                                            borderTop: "1px solid var(--line)",
                                        }}
                                        footer={
                                            !loading && response?.pagination ? (
                                                <PaginationNew
                                                    currentPage={response.pagination.current_page}
                                                    handlePageChange={handleMeetingPageChange}
                                                    totalPages={response.pagination.total_pages}
                                                    paginationLength={totalItems}
                                                    pageSize={meetingPageSize}
                                                    onPageSizeChange={handleMeetingPageSizeChange}
                                                />
                                            ) : null
                                        }
                                    />

                                    <SearchableTable
                                        title="Answered in session"
                                        count={totalEmailItems || 0}
                                        columns={emailColumns}
                                        rows={customerEmails}
                                        getRowKey={(e) =>
                                            e.thread_id ?? `${e.agency_id}-${e.employee_id}`
                                        }
                                        loading={emailLoading}
                                        error={emailError}
                                        loadingMessage="Loading customer emails..."
                                        errorMessage="Failed to load customer emails. Please try again."
                                        emptyMessage="No answered email conversations found."
                                        footerClassName="absolute bottom-[-16px] right-0 w-full"
                                        footerStyle={{
                                            padding: "12px 16px",
                                            borderTop: "1px solid var(--line)",
                                        }}
                                        footer={
                                            !emailLoading && emailResponse?.pagination ? (
                                                <PaginationNew
                                                    currentPage={emailResponse.pagination.current_page}
                                                    handlePageChange={handleEmailPageChange}
                                                    totalPages={emailResponse.pagination.total_pages}
                                                    paginationLength={totalEmailItems}
                                                    pageSize={emailPageSize}
                                                    onPageSizeChange={handleEmailPageSizeChange}
                                                />
                                            ) : null
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {selectedMeeting && (
                <div
                    onClick={() => setSelectedMeeting(null)}
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(15, 23, 42, 0.55)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "20px",
                        zIndex: 9999,
                    }}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="meeting-details-title"
                        onClick={(event) => event.stopPropagation()}
                        style={{
                            width: "100%",
                            maxWidth: "800px",
                            maxHeight: "85vh",
                            overflowY: "auto",
                            background: "#fff",
                            borderRadius: "12px",
                            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
                        }}
                    >
                        {/* Popup Header */}
                        <div
                            style={{
                                padding: "20px 24px",
                                borderBottom: "1px solid var(--line)",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: "16px",
                                position: "sticky",
                                top: 0,
                                background: "#fff",
                                zIndex: 1,
                            }}
                        >
                            <div>
                                <h2
                                    id="meeting-details-title"
                                    style={{
                                        margin: 0,
                                        fontSize: "20px",
                                        fontWeight: 700,
                                        color: "var(--ink)",
                                    }}
                                >
                                    Meeting Details
                                </h2>

                                <p
                                    style={{
                                        margin: "5px 0 0",
                                        color: "var(--ink-2)",
                                        fontSize: "13px",
                                    }}
                                >
                                    {selectedMeeting.agency_name || "Unknown Agency"}
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() => setSelectedMeeting(null)}
                                aria-label="Close meeting details"
                                className="text-gray-400 cta-red transition mt-0.5 ml-4 flex-shrink-0 pt-1"
                            >
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M6 18L18 6M6 6l12 12"
                                    ></path>
                                </svg>
                            </button>
                        </div>

                        {/* Popup Content */}
                        <div style={{ padding: "24px" }}>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                                    gap: "20px",
                                }}
                            >
                                <div className="block  gap-2">
                                    <label className=" meta-tag self-stretch relative font-semibold dark:text-white">
                                        Agency Name
                                    </label>
                                    <div className="inputborder_css_dv mt-id-dv self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] mt-2 rounded-radius-md bg-text-text-tertiary overflow-hidden flex flex-row items-center justify-start py-2.5 px-[15px] dark:bg-slate-700 border-[1px] border-solid  border-surface-border undefined ">
                                        {selectedMeeting.agency_name || "--"}
                                    </div>
                                </div>

                                <div className="block  gap-2">
                                    <label className=" meta-tag self-stretch relative font-semibold dark:text-white">
                                        Participant Name
                                    </label>
                                    <div className="inputborder_css_dv mt-id-dv self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] mt-2 rounded-radius-md bg-text-text-tertiary overflow-hidden flex flex-row items-center justify-start py-2.5 px-[15px] dark:bg-slate-700 border-[1px] border-solid  border-surface-border undefined ">
                                        {formatParticipantName(selectedMeeting.participant_name)}
                                    </div>
                                </div>

                                <div className="block  gap-2">
                                    <label className=" meta-tag self-stretch relative font-semibold dark:text-white">
                                        Meeting Type
                                    </label>
                                    <div className="inputborder_css_dv mt-id-dv self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] mt-2 rounded-radius-md bg-text-text-tertiary overflow-hidden flex flex-row items-center justify-start py-2.5 px-[15px] dark:bg-slate-700 border-[1px] border-solid  border-surface-border undefined ">
                                        {selectedMeeting.meeting_type || "--"}
                                    </div>
                                </div>

                                <div className="block  gap-2">
                                    <label className=" meta-tag self-stretch relative font-semibold dark:text-white">
                                        Meeting Date
                                    </label>
                                    <div className="inputborder_css_dv mt-id-dv self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] mt-2 rounded-radius-md bg-text-text-tertiary overflow-hidden flex flex-row items-center justify-start py-2.5 px-[15px] dark:bg-slate-700 border-[1px] border-solid  border-surface-border undefined ">
                                        {formatMeetingDate(selectedMeeting.meeting_date)}
                                    </div>
                                </div>

                                <div className="block  gap-2">
                                    <label className=" meta-tag self-stretch relative font-semibold dark:text-white">
                                        Meeting Time
                                    </label>

                                    <div className="inputborder_css_dv mt-id-dv self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] mt-2 rounded-radius-md bg-text-text-tertiary overflow-hidden flex flex-row items-center justify-start py-2.5 px-[15px] dark:bg-slate-700 border-[1px] border-solid  border-surface-border undefined ">
                                        {formatMeetingTime(selectedMeeting.meeting_time_ist)}
                                    </div>
                                </div>

                                <div className="block  gap-2">
                                    <label className=" meta-tag self-stretch relative font-semibold dark:text-white">
                                        Meeting Duration
                                    </label>
                                    <div className="inputborder_css_dv mt-id-dv self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] mt-2 rounded-radius-md bg-text-text-tertiary overflow-hidden flex flex-row items-center justify-start py-2.5 px-[15px] dark:bg-slate-700 border-[1px] border-solid  border-surface-border undefined ">
                                        {selectedMeeting.meeting_duration || "--"}
                                    </div>
                                </div>
                            </div>

                            {/* Structured meeting sections (Transcript is intentionally hidden). */}
                            <MeetingOverviewSections
                                transcript={selectedMeeting.transcript}
                                meetingSummary={selectedMeeting.meeting_summary}
                                actionItems={selectedMeeting.action_items}
                            />

                            {/* Recording URL */}
                            <div style={{ marginTop: "24px" }}>
                                <h5 className="relative font-bold text-xl-dv  mt-0 mb-[16px] capitalize dark:text-white">
                                    Recording URL
                                </h5>

                                {selectedMeeting.recording_url ? (
                                    <a
                                        href={selectedMeeting.recording_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            color: "var(--purple-deep)",
                                            textDecoration: "underline",
                                            overflowWrap: "anywhere",
                                            fontSize: "13px",
                                        }}
                                    >
                                        Open Meeting Recording ↗
                                    </a>
                                ) : (
                                    <p className="muted-text">Recording URL not available.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Customer Email Conversation Popup */}
            {selectedEmail && (
                <div
                    onClick={() => setSelectedEmail(null)}
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(15, 23, 42, 0.55)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "20px",
                        zIndex: 10000,
                    }}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="email-details-title"
                        onClick={(event) => event.stopPropagation()}
                        style={{
                            width: "100%",
                            maxWidth: "900px",
                            maxHeight: "85vh",
                            overflowY: "auto",
                            background: "#fff",
                            borderRadius: "12px",
                            boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
                        }}
                    >
                        {/* Header */}
                        <div
                            style={{
                                padding: "20px 24px",
                                borderBottom: "1px solid var(--line)",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: "16px",
                                position: "sticky",
                                top: 0,
                                background: "#fff",
                                zIndex: 1,
                            }}
                        >
                            <div>
                                <h2
                                    id="email-details-title"
                                    style={{
                                        margin: 0,
                                        fontSize: "20px",
                                        fontWeight: 700,
                                        color: "var(--ink)",
                                    }}
                                >
                                    Email Conversation
                                </h2>
                            </div>

                            <button
                                type="button"
                                onClick={() => setSelectedEmail(null)}
                                aria-label="Close email conversation"
                                className="text-gray-400 cta-red transition"
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ padding: "24px" }}>
                            {/* Agency Information */}
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                                    gap: "20px",
                                }}
                            >
                                {[
                                    {
                                        label: "Agency Name",
                                        value: selectedEmail.agency_name,
                                    },
                                    {
                                        label: "Agency Email",
                                        value: selectedEmail.agency_email,
                                    },
                                    {
                                        label: "Replied By",
                                        value: selectedEmail.replied_by,
                                    },
                                ].map(({ label, value }) => (
                                    <div className="grid gap-2" key={label}>
                                        <label className="meta-tag font-semibold">{label}</label>

                                        <div
                                            style={{
                                                padding: "10px 14px",
                                                border: "1px solid var(--line)",
                                                borderRadius: "8px",
                                                background: "var(--page)",
                                                color: "var(--ink)",
                                                overflowWrap: "anywhere",
                                                minHeight: "20px",
                                            }}
                                        >
                                            {value || "--"}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Complete Conversation */}
                            <div style={{ marginTop: "28px" }}>
                                <h3
                                    style={{
                                        margin: "0 0 16px",
                                        fontSize: "17px",
                                        fontWeight: 700,
                                        color: "var(--ink)",
                                    }}
                                >
                                    Complete Conversation
                                </h3>

                                {(() => {
                                    const messages = [...(selectedEmail.conversation ?? [])].sort(
                                        (a, b) => {
                                            const timeA = a.time_ist || a.time;
                                            const timeB = b.time_ist || b.time;

                                            const dateA = timeA ? new Date(timeA).getTime() : 0;
                                            const dateB = timeB ? new Date(timeB).getTime() : 0;

                                            return dateA - dateB;
                                        },
                                    );

                                    return messages.length > 0 ? (
                                        <div style={{ display: "grid", gap: "16px" }}>
                                            {messages.map((message, index) => {
                                                const type = (message.type || "").toLowerCase();
                                                const isSupport =
                                                    type.includes("support") ||
                                                    type.includes("reply") ||
                                                    type.includes("sent") ||
                                                    type.includes("outgoing");

                                                const sender = isSupport
                                                    ? message.sender
                                                        ? `Support (${message.sender})`
                                                        : "Support"
                                                    : type.includes("customer") ||
                                                        type.includes("incoming") ||
                                                        type.includes("received")
                                                        ? "Customer"
                                                        : message.sender || "Customer";

                                                return (
                                                    <div
                                                        key={`${selectedEmail.thread_id}-${index}`}
                                                        style={{
                                                            padding: "16px",
                                                            borderRadius: "10px",
                                                            border: "1px solid var(--line)",
                                                            background: isSupport ? "#F3EFFB" : "#F5F6FA",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "baseline",
                                                                flexWrap: "wrap",
                                                                gap: "8px",
                                                                marginBottom: "10px",
                                                            }}
                                                        >
                                                            <strong
                                                                style={{
                                                                    color: isSupport
                                                                        ? "var(--purple-deep)"
                                                                        : "var(--ink)",
                                                                    fontSize: "13px",
                                                                }}
                                                            >
                                                                {sender}
                                                            </strong>

                                                            <span
                                                                style={{
                                                                    fontSize: "12px",
                                                                    color: "var(--ink-2)",
                                                                }}
                                                            >
                                                                {formatEmailTimestamp(
                                                                    message.time_ist || message.time,
                                                                )}
                                                            </span>
                                                        </div>

                                                        <div
                                                            style={{
                                                                fontSize: "13px",
                                                                lineHeight: 1.8,
                                                                color: "var(--ink)",
                                                                whiteSpace: "pre-wrap",
                                                                overflowWrap: "anywhere",
                                                            }}
                                                        >
                                                            {message.body?.trim() ||
                                                                "No message content available."}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="muted-text">
                                            No conversation messages available.
                                        </p>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <DefectEmailModal
                defectsData={defectsData}
                isOpen={openDefectEmail}
                onClose={() => setOpenDefectEmail("")}
            />
        </>
    );
};

export default ActionsTable;